import { useEffect, type ReactNode } from "react";
import { useAuthStore } from "@/stores/authStore";
import { getSupabase } from "@/lib/supabase";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, setSession, clearSession } = useAuthStore();

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    async function initAuth() {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          setSession(session);
        } else {
          clearSession();
          // In dev mode, redirect to main site login
          const loginUrl = import.meta.env.DEV
            ? "http://localhost:8888/login"
            : "/login";
          window.location.href = loginUrl;
          return; // Prevent further execution
        }

        // Listen for auth changes
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === "SIGNED_OUT" || !session) {
            clearSession();
            const loginUrl = import.meta.env.DEV
              ? "http://localhost:8888/login"
              : "/login";
            window.location.href = loginUrl;
          } else {
            setSession(session);
          }
        });

        unsubscribe = () => subscription.unsubscribe();
      } catch (error) {
        console.error("Auth initialization failed:", error);
        clearSession();
        const loginUrl = import.meta.env.DEV
          ? "http://localhost:8888/login"
          : "/login";
        window.location.href = loginUrl;
      }
    }

    initAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [setSession, clearSession]);

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    // Show redirecting state instead of blank page
    // This prevents white flash while redirect is happening
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-[var(--color-bg)] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--color-primary)] border-t-transparent" />
        <p className="text-[var(--color-text-muted)] text-sm">Redirecting to login...</p>
      </div>
    );
  }

  return <>{children}</>;
}
